import React, { useRef, useEffect } from "react";
import { Container, Typography, Box, Grid, Chip, LinearProgress } from "@mui/material";
import { Link } from "react-router-dom";
import * as THREE from "three";
import ParticleComponent from "./ParticleComponent.js";
import FOG from "vanta/dist/vanta.fog.min.js";

const categoryColors = {
  frontend: { bg: "rgba(96, 165, 250, 0.10)", text: "#3b82f6", border: "rgba(96, 165, 250, 0.25)", bar: "#3b82f6" },
  backend: { bg: "rgba(52, 211, 153, 0.10)", text: "#10b981", border: "rgba(52, 211, 153, 0.25)", bar: "#10b981" },
  devops: { bg: "rgba(251, 191, 36, 0.10)", text: "#b45309", border: "rgba(251, 191, 36, 0.25)", bar: "#f59e0b" },
  creative: { bg: "rgba(192, 132, 252, 0.10)", text: "#8b5cf6", border: "rgba(192, 132, 252, 0.25)", bar: "#8b5cf6" },
  gamedev: { bg: "rgba(244, 114, 182, 0.10)", text: "#db2777", border: "rgba(244, 114, 182, 0.25)", bar: "#ec4899" },
  ai: { bg: "rgba(45, 212, 191, 0.10)", text: "#0d9488", border: "rgba(45, 212, 191, 0.25)", bar: "#14b8a6" },
};

const maxYears = 14;

const skillGroups = [
  {
    category: "frontend",
    label: "Frontend",
    skills: [
      { name: "JavaScript", years: 14, projects: ["NYSHEX", "Contracts.org", "OhNo.tv", "SpeechSynth StreamChat"] },
      { name: "HTML / CSS", years: 14, projects: ["NYSHEX", "Contracts.org", "Parlay.bot", "SpeechSynth StreamChat"] },
      { name: "React", years: 5, projects: ["NYSHEX", "Parlay.bot", "SpeechSynth StreamChat"] },
      { name: "Material-UI", years: 3, projects: ["NYSHEX", "SpeechSynth StreamChat"] },
      { name: "Three.JS", years: 1, projects: ["Action Face"] },
    ],
  },
  {
    category: "backend",
    label: "Backend",
    skills: [
      { name: "REST APIs", years: 12, projects: ["NYSHEX", "Parlay.bot", "SpeechSynth StreamChat", "Contracts.org"] },
      { name: "Ruby on Rails", years: 6, projects: ["Action Face", "Contracts.org", "OhNo.tv"] },
      { name: "Python / Flask", years: 6, projects: ["Intel", "Parlay.bot"] },
      { name: "Node.js", years: 6, projects: ["NYSHEX", "SpeechSynth StreamChat"] },
      { name: "PostgreSQL / MySQL", years: 6, projects: ["Contracts.org", "Parlay.bot", "OhNo.tv"] },
    ],
  },
  {
    category: "devops",
    label: "DevOps & Tools",
    skills: [
      { name: "Agile / Scrum", years: 10, projects: ["NYSHEX", "Intel"] },
      { name: "Git", years: 8, projects: ["NYSHEX", "Parlay.bot", "OhNo.tv", "SpeechSynth StreamChat"] },
      { name: "JIRA", years: 8, projects: ["NYSHEX", "Intel"] },
      { name: "Linux", years: 6, projects: ["Intel"] },
      { name: "Heroku", years: 5, projects: ["Contracts.org", "Parlay.bot", "OhNo.tv"] },
    ],
  },
  {
    category: "gamedev",
    label: "Game Development",
    skills: [
      { name: "Unreal Engine", years: 2, projects: ["Game Dev"] },
      { name: "Unity / C#", years: 3, projects: ["Game Dev"] },
      { name: "Blueprints", years: 2, projects: ["Game Dev"] },
      { name: "Blender", years: 2, projects: ["Game Dev"] },
    ],
  },
  {
    category: "creative",
    label: "Creative & Production",
    skills: [
      { name: "After Effects", years: 8, projects: ["Video Work"] },
      { name: "Adobe Premiere", years: 8, projects: ["Video Work"] },
      { name: "Photoshop", years: 12, projects: ["Game Dev", "Video Work"] },
    ],
  },
  {
    category: "ai",
    label: "AI & Emerging Tech",
    skills: [
      { name: "Machine Learning", years: 2, projects: ["Parlay.bot"] },
      { name: "ChatGPT / LLMs", years: 2, projects: ["Parlay.bot"] },
      { name: "AWS Polly", years: 2, projects: ["SpeechSynth StreamChat"] },
      { name: "Blockchain / Solidity", years: 1, projects: ["OhNo.tv"] },
    ],
  },
];

const Skills = () => {
  const vantaRef = useRef(null);

  useEffect(() => {
    let vantaEffect = null;
    vantaEffect = FOG({
      el: vantaRef.current,
      THREE: THREE,
      mouseControls: true,
      touchControls: true,
      gyroControls: false,
      highlightColor: 0xf0b01,
      midtoneColor: 0x69d82,
      baseColor: 0x0,
      speed: 3,
    });

    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, []);

  return (
    <>
      <div style={{ position: "absolute", top: 0, zIndex: 0 }}>
        <ParticleComponent width="50%" height="50%" />
      </div>
      <Box ref={vantaRef} sx={{ minHeight: "100vh", py: 4 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            sx={{ color: "white", mb: 4 }}
          >
            Skills & Experience
          </Typography>

          <Grid container spacing={3}>
            {skillGroups.map((group) => {
              const colors = categoryColors[group.category];
              return (
                <Grid item xs={12} md={6} key={group.category}>
                  <Box
                    sx={{
                      backgroundColor: "rgba(240, 240, 240, 0.9)",
                      backdropFilter: "blur(10px)",
                      borderRadius: 2,
                      p: 2.5,
                      height: "100%",
                      borderTop: `3px solid ${colors.bar}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: colors.text,
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      {group.label}
                    </Typography>

                    {group.skills.map((skill) => (
                      <Box key={skill.name} sx={{ mb: 2 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={0.5}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500 }}
                          >
                            {skill.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            {skill.years} yr{skill.years !== 1 ? "s" : ""}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(skill.years / maxYears) * 100}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "rgba(0,0,0,0.06)",
                            mb: 0.5,
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 3,
                              backgroundColor: colors.bar,
                            },
                          }}
                        />
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {skill.projects.map((proj) => (
                            <Chip
                              key={proj}
                              label={proj}
                              size="small"
                              component={Link}
                              to="/work"
                              clickable
                              sx={{
                                height: "20px",
                                fontSize: "0.65rem",
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `1px solid ${colors.border}`,
                                textDecoration: "none",
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Skills;
